import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FaArrowRight, FaAward, FaUsers, FaNewspaper, FaLinkedin } from 'react-icons/fa';
import EditableText from '../components/EditableText';

const team = [
  {
    name: 'M.A. Zaman',
    role: 'Chairman & Group CEO',
    initials: 'MAZ',
    image: '/team-maz.png',
    bio: 'Visionary entrepreneur with 35 years experience in building technology businesses valued over USD 10 Billion. Former Sr Exec. VP of Millicom Intl. cellular S.A. and VP of Kinnevik Group, in charge of all Asia-Pacific Operations.'
  },
  {
    name: 'Conny Karlsson',
    role: 'Chief Operating Officer',
    initials: 'CK',
    image: '/team-ck.png',
    bio: 'M.Sc.E.E from Royal Institute of Technology and Executive MBA from Stockholm School of Economics. 20 years experience from technology projects in more than 50 countries within telecom, fintech and digital transformation.',
    linkedin: 'https://www.linkedin.com/in/karlssonconny/'
  },
  {
    name: 'Rui Gomes',
    role: 'Chief Technology Officer',
    initials: 'RG',
    image: '/team-rg.jpg',
    bio: 'Former Director of IT for deCode/Amgen. Former Director of HPC/AI Operations for atNorth. Former CTO for RVX working on major film productions including Gravity, Everest, 2 Guns, and Harry Potter.',
    linkedin: 'https://www.linkedin.com/in/rugomes/'
  },
  {
    name: 'Thomas Ekman',
    role: 'Board Director',
    initials: 'TE',
    image: '/team-te.png',
    bio: 'Former President and CEO of Axel Johnson Group, Former President and CEO of Dustin. Former CEO Tele2 Sverige AB & Market Area Director Nordic. Former General Manager MTG Group.',
    linkedin: 'https://www.linkedin.com/in/tekman/'
  },
  {
    name: 'Staffan Holm',
    role: 'Board Director',
    initials: 'SH',
    image: '/team-sh.png',
    bio: 'Former CEO and Chairman of Live Nation Sweden. Developed ABBA the Museum and Mamma Mia the Party. Chairman and major shareholder of Tunigo playlist discovery app, later sold to Spotify.',
    linkedin: 'https://www.linkedin.com/in/staffan-holm-87323438/'
  },
  {
    name: 'Johnny Svedberg',
    role: 'Board Director',
    initials: 'JS',
    image: '/team-js.png',
    bio: 'Former Executive Vice President and Market Area Director Eastern Europe for Tele2 Group. Former Board Director and COO for IndosatOoredoo in Indonesia with USD 2.2B revenue and 100M subscribers.',
    linkedin: 'https://www.linkedin.com/in/johnny-svedberg-7485898/'
  },
  {
    name: 'Guido Wagenaar',
    role: 'SVP M&A',
    initials: 'GW',
    image: '/team-gw.png',
    bio: 'Over 25 years of international executive experience in corporate structuring, M&A and strategic growth across technology and financial services. Built, scaled and successfully exited multiple global businesses, with extensive experience in cross-border transactions and large-scale financing involving institutional investors and global banks.',
    linkedin: 'https://www.linkedin.com/in/guido-wagenaar-756530/'
  },
  {
    name: 'Johan Dennelind',
    role: 'Senior Strategic Advisor',
    initials: 'JD',
    image: '/team-jd.png',
    bio: 'Former President and CEO of Telia Company. Former CEO of du and various CEO/CFO positions within Telenor Group. CEO Vodacom International/Vodafone. MSc in Finance & Business Administration. Advisor and investor in Nordic tech.',
    linkedin: 'https://www.linkedin.com/in/johandennelind/'
  },
  {
    name: 'Duke Aschan',
    role: 'Board Advisor',
    initials: 'DA',
    image: '/team-da.png',
    bio: 'Experienced Board Member with 10 years experience in Datacenter and HPC at T.Loop, atNorth and Interxion. Holds an MBA in International Business from Uppsala University.',
    linkedin: 'https://www.linkedin.com/in/dukeaschan/'
  },
  {
    name: 'Mats Holmfeldt',
    role: 'BOARD ADVISOR',
    initials: 'MH',
    image: '/team-mh.png',
    bio: '30 years in Banking and Fintech, Sr. Executive and Board Director in SEB, Swedbank, Intrum and Resurs Bank. Start-up incubation and innovation companies mainly within Fintech sector.',
    linkedin: 'https://www.linkedin.com/in/matsholmfeldt/'
  }
];

function About() {
  const [content, setContent] = useState({});

  useEffect(() => {
    fetch('/api/content?page=about')
      .then(res => res.json())
      .then(data => setContent(data))
      .catch(() => {});
  }, []);

  return (
    <>
      <Helmet>
        <title>About Us | Leadership Team | 6G AI Sweden</title>
        <meta name="description" content="Learn about 6G AI Sweden AB, our leadership team, and our mission to bring sovereign AI capabilities to Swedish businesses." />
        <meta name="keywords" content="6G AI Sweden, about us, leadership team, Swedish AI company, AI infrastructure" />
        <meta property="og:title" content="About Us | 6G AI Sweden" />
        <meta property="og:description" content="Meet the leadership team behind 6G AI Sweden." />
      </Helmet>

      {/* Page Header */}
      <section className="page-header">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="page-title"><EditableText section="page_title" page="about" defaultValue="About Us" as="span" /></h1>
              <p className="page-subtitle">
                <EditableText section="page_subtitle" page="about" defaultValue="Pioneering sovereign AI infrastructure in Sweden" as="span" />
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Company Overview */}
      <section className="section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h2 className="section-title text-start"><EditableText section="overview_title" page="about" defaultValue="Your AI Factory in Sweden" as="span" /></h2>
              <p style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '1.1rem', marginBottom: '1.5rem' }}>
                <EditableText section="overview_desc_1" page="about" defaultValue="6G AI Sweden AB is a leading Swedish AI cloud provider, dedicated to advancing the capabilities of artificial intelligence through innovative cloud solutions. Based in Sweden, we are committed to delivering secure, efficient, and scalable AI services that empower businesses and drive technological growth." as="span" />
              </p>
              <p style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '1.1rem', marginBottom: '2rem' }}>
                <EditableText section="overview_desc_2" page="about" defaultValue="Our mission is to integrate cutting-edge AI technologies into the cloud environment, ensuring seamless accessibility and robust performance. The 6G AI Factory offers a full range of AI cloud services that will help you get the most benefit for your business." as="span" />
              </p>
            </Col>
            <Col lg={6}>
              <div style={{ 
                background: 'var(--md-sys-color-surface-container)', 
                borderRadius: 'var(--radius-large)', 
                padding: '2rem',
                border: '1px solid var(--md-sys-color-outline-variant)'
              }}>
                <h4 style={{ marginBottom: '1.5rem' }}><EditableText section="why_title" page="about" defaultValue="Why Swedish AI Infrastructure?" as="span" /></h4>
                <ul className="service-features" style={{ fontSize: '1rem' }}>
                  <li><EditableText section="why_list_1" page="about" defaultValue="Data sovereignty under Swedish jurisdiction" as="span" /></li>
                  <li><EditableText section="why_list_2" page="about" defaultValue="Full GDPR and EU AI Act compliance" as="span" /></li>
                  <li><EditableText section="why_list_3" page="about" defaultValue="ISO/IEC 27001:2022 certified" as="span" /></li>
                  <li><EditableText section="why_list_4" page="about" defaultValue="100% renewable energy powered" as="span" /></li>
                  <li><EditableText section="why_list_5" page="about" defaultValue="Strategic Nordic location" as="span" /></li>
                  <li><EditableText section="why_list_6" page="about" defaultValue="World-class research ecosystem" as="span" /></li>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* News */}
      <section className="section section-dark">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div style={{ 
                background: 'linear-gradient(135deg, rgba(123,44,191,0.1) 0%, rgba(157,78,221,0.05) 100%)',
                borderRadius: 'var(--radius-large)',
                padding: '2.5rem',
                border: '1px solid var(--md-sys-color-primary-container)',
                textAlign: 'center'
              }}>
                <FaNewspaper size={40} style={{ color: 'var(--md-sys-color-primary)', marginBottom: '1rem' }} />
                <h3 style={{ marginBottom: '1rem' }}><EditableText section="news_title" page="about" defaultValue="Featured in Dagens Industri" as="span" /></h3>
                <p style={{ color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '1.5rem' }}>
                  <EditableText section="news_desc" page="about" defaultValue="&quot;Telekomjättarnas miljardbygge utmanar USA:s techjättar&quot; — July 3rd, 2025" as="span" />
                </p>
                <Button 
                  variant="outline-light" 
                  size="lg" 
                  href="https://www.di.se/digital/telekomravarnas-miljardbygge-utmanar-usa-s-techjattar/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read Article <FaArrowRight />
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Leadership Team */}
      <section className="section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title"><EditableText section="team_title" page="about" defaultValue="Leadership Team" as="span" /></h2>
            <p className="section-subtitle"><EditableText section="team_subtitle" page="about" defaultValue="Experienced leaders driving Sweden's AI future" as="span" /></p>
          </div>
          <Row>
            {team.map((member, index) => (
              <Col md={6} lg={3} key={index} className="mb-4">
                {member.linkedin ? (
                  <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="team-card-link">
                    <div className="team-card">
                      {member.image ? (
                        <img 
                          src={process.env.PUBLIC_URL + member.image} 
                          alt={member.name}
                          loading="lazy"
                          style={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            objectPosition: member.name === 'M.A. Zaman' ? 'center 30%' : 'center',
                            margin: '0 auto 1.5rem',
                            border: '2px solid rgba(157, 78, 221, 0.3)',
                          }}
                        />
                      ) : (
                        <div className="team-avatar">{member.initials}</div>
                      )}
                      <h4 className="team-name"><EditableText section={`team_${index}_name`} page="about" defaultValue={member.name} as="span" /></h4>
                      <p className="team-role"><EditableText section={`team_${index}_role`} page="about" defaultValue={member.role} as="span" /></p>
                      <p className="team-bio"><EditableText section={`team_${index}_bio`} page="about" defaultValue={member.bio} as="span" /></p>
                      <span className="team-linkedin-btn">
                        <FaLinkedin /> LinkedIn Profile
                      </span>
                    </div>
                  </a>
                ) : (
                  <div className="team-card">
                    {member.image ? (
                      <img 
                        src={process.env.PUBLIC_URL + member.image} 
                        alt={member.name}
                        style={{
                          width: '100px',
                          height: '100px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          objectPosition: member.name === 'M.A. Zaman' ? 'center 30%' : 'center',
                          margin: '0 auto 1.5rem',
                          border: '2px solid rgba(157, 78, 221, 0.3)',
                        }}
                      />
                    ) : (
                      <div className="team-avatar">{member.initials}</div>
                    )}
                    <h4 className="team-name"><EditableText section={`team_${index}_name`} page="about" defaultValue={member.name} as="span" /></h4>
                    <p className="team-role"><EditableText section={`team_${index}_role`} page="about" defaultValue={member.role} as="span" /></p>
                    <p className="team-bio"><EditableText section={`team_${index}_bio`} page="about" defaultValue={member.bio} as="span" /></p>
                  </div>
                )}
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="section section-dark">
        <Container>
          <div className="cta-section">
            <h2 className="cta-title">Join Sweden's AI Revolution</h2>
            <p className="cta-text">
              Partner with us to accelerate your AI initiatives with sovereign, sustainable infrastructure.
            </p>
            <Button variant="primary" size="lg" as={Link} to="/contact">
              Get in Touch <FaArrowRight />
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
}

export default About;
